/**
 * ContentForge — Tasks Board View (Kanban)
 *
 * Displays tasks in a Kanban board with columns for each status.
 * Supports creating new tasks and viewing task details.
 */

import { html, nothing } from "lit";
import type { CfTask, CfTaskStatus, CfAgentType } from "../controllers/contentforge.ts";

// ─── Types ──────────────────────────────────────────────────────────────

export type CfTasksProps = {
  tasks: CfTask[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  onCreate: (title: string, agentType: CfAgentType) => void;
};

// ─── Constants ──────────────────────────────────────────────────────────

const COLUMNS: { key: CfTaskStatus; label: string; accent: string }[] = [
  { key: "recurring", label: "Recurring", accent: "var(--cf-accent-purple, #a78bfa)" },
  { key: "backlog", label: "Backlog", accent: "var(--cf-accent-blue, #60a5fa)" },
  { key: "in-progress", label: "In Progress", accent: "var(--cf-accent-yellow, #fbbf24)" },
  { key: "review", label: "Review", accent: "var(--cf-accent-orange, #fb923c)" },
  { key: "done", label: "Done", accent: "var(--cf-accent-green, #34d399)" },
];

const AGENT_TYPES: { value: CfAgentType; label: string }[] = [
  { value: "boss", label: "Boss" },
  { value: "strategist", label: "Strategist" },
  { value: "sourcer", label: "Sourcer" },
  { value: "writer", label: "Writer" },
  { value: "artist", label: "Artist" },
  { value: "publisher", label: "Publisher" },
  { value: "auditor", label: "Auditor" },
  { value: "miner", label: "Miner" },
];

// ─── Helpers ────────────────────────────────────────────────────────────

function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) {
    return "just now";
  }
  if (mins < 60) {
    return `${mins}m ago`;
  }
  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function agentBadge(agentType: string): string {
  const map: Record<string, string> = {
    boss: "B",
    strategist: "S",
    sourcer: "Sr",
    writer: "W",
    artist: "A",
    publisher: "P",
    auditor: "Au",
    miner: "M",
  };
  return map[agentType] ?? agentType.slice(0, 2).toUpperCase();
}

// ─── Render ─────────────────────────────────────────────────────────────

export function renderCfTasks(props: CfTasksProps) {
  const { tasks, loading, error, onRefresh, onCreate } = props;

  // Stats
  const total = tasks.length;
  const inProgress = tasks.filter((t) => t.status === "in-progress").length;
  const done = tasks.filter((t) => t.status === "done").length;
  const completionPct = total > 0 ? Math.round((done / total) * 100) : 0;

  return html`
    <div class="cf-tasks">
      <!-- Header -->
      <div class="cf-tasks__header">
        <div class="cf-tasks__title">
          <h2>Tasks</h2>
          <button class="cf-btn cf-btn--sm cf-btn--ghost" @click=${onRefresh}>
            Refresh
          </button>
        </div>
        ${error ? html`<div class="cf-error">${error}</div>` : nothing}
      </div>

      <!-- Stats Bar -->
      <div class="cf-stats">
        <div class="cf-stats__item">
          <span class="cf-stats__value">${total}</span>
          <span class="cf-stats__label">Total</span>
        </div>
        <div class="cf-stats__item">
          <span class="cf-stats__value">${inProgress}</span>
          <span class="cf-stats__label">In Progress</span>
        </div>
        <div class="cf-stats__item">
          <span class="cf-stats__value">${done}</span>
          <span class="cf-stats__label">Done</span>
        </div>
        <div class="cf-stats__item">
          <span class="cf-stats__value">${completionPct}%</span>
          <span class="cf-stats__label">Completion</span>
        </div>
      </div>

      <!-- New Task Form -->
      ${renderNewTaskForm(onCreate)}

      <!-- Loading -->
      ${
        loading
          ? html`
              <div class="cf-loading">Loading tasks…</div>
            `
          : nothing
      }

      <!-- Kanban Board -->
      <div class="cf-kanban">
        ${COLUMNS.map((col) => {
          const colTasks = tasks.filter((t) => t.status === col.key);
          return html`
            <div class="cf-kanban__column">
              <div class="cf-kanban__column-header">
                <span class="cf-kanban__column-dot" style="background: ${col.accent}"></span>
                <span class="cf-kanban__column-title">${col.label}</span>
                <span class="cf-kanban__column-count">${colTasks.length}</span>
              </div>
              <div class="cf-kanban__cards">
                ${
                  colTasks.length === 0
                    ? html`
                        <div class="cf-kanban__empty">No tasks</div>
                      `
                    : colTasks.map((task) => renderTaskCard(task))
                }
              </div>
            </div>
          `;
        })}
      </div>
    </div>
  `;
}

// ─── New Task Form ──────────────────────────────────────────────────────

function renderNewTaskForm(onCreate: (title: string, agentType: CfAgentType) => void) {
  return html`
    <div class="cf-new-task">
      <input
        type="text"
        class="cf-input"
        id="cf-new-task-title"
        placeholder="New task title…"
        @keydown=${(e: KeyboardEvent) => {
          if (e.key === "Enter") {
            const input = e.target as HTMLInputElement;
            const select = document.getElementById("cf-new-task-agent") as HTMLSelectElement;
            const title = input.value.trim();
            if (!title) {
              return;
            }
            onCreate(title, (select?.value ?? "boss") as CfAgentType);
            input.value = "";
          }
        }}
      />
      <select class="cf-select" id="cf-new-task-agent">
        ${AGENT_TYPES.map((a) => html`<option value=${a.value}>${a.label}</option>`)}
      </select>
      <button
        class="cf-btn cf-btn--primary"
        @click=${() => {
          const input = document.getElementById("cf-new-task-title") as HTMLInputElement;
          const select = document.getElementById("cf-new-task-agent") as HTMLSelectElement;
          const title = input?.value.trim();
          if (!title) {
            return;
          }
          onCreate(title, (select?.value ?? "boss") as CfAgentType);
          input.value = "";
        }}
      >
        + New Task
      </button>
    </div>
  `;
}

// ─── Task Card ──────────────────────────────────────────────────────────

function renderTaskCard(task: CfTask) {
  return html`
    <div class="cf-kanban__card">
      <div class="cf-kanban__card-title">${task.title}</div>
      <div class="cf-kanban__card-meta">
        <span class="cf-kanban__card-agent" title=${task.agentType}>
          ${agentBadge(task.agentType)}
        </span>
        <span class="cf-kanban__card-time">${formatRelativeTime(task.createdAt)}</span>
      </div>
      ${task.error ? html`<div class="cf-kanban__card-error">${task.error}</div>` : nothing}
    </div>
  `;
}
